import React, { PureComponent } from 'react';
import '@client/polyfills/canvasToBlob';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import styleHelper from '@client/shared/styles/styleHelper.scss';
import { valueOrEmptyStr } from '@client/shared/utils/utils/utils';
import { getIn } from '@client/shared/utils/immutableUtils/immutableUtils';
import FormSectionContainer from '@client/containers/formSectionContainer/FormSectionContainer';
import InputGroup from '@client/components/formComponents/inputGroup/InputGroup';
import LabelWrapper from '@client/components/formComponents/labelWrapper/LabelWrapper';
import Markdown from '@client/components/markdown/Markdown';
import { ImageUploader } from '@client/components/fileUploaders/imageUploader/imageUploader';
import Checkbox from '@client/components/formComponents/checkbox/Checkbox';
import ContentResponsibilityPolicyText from '@client/containers/contentResponsibilityPolicyText/ContentResponsibilityPolicyText';
import {
  getFundraiserTypeId,
  isMemorialFundraiser,
  isSporteventFundraiser,
} from '@client/shared/utils/fundraiserUtils/fundraiserUtils';
import { getSearchQueryValue } from '@client/shared/utils/urlUtils/urlUtils';
import {
  FUNDRAISER_START_DATE,
  FUNDRAISER_END_DATE,
  FUNDRAISER_CURRENT,
  FUNDRAISER_ACCEPT_CONTENT_RESPONSIBILITY_LABEL,
  FUNDRAISER_ACCEPT_CONTENT_RESPONSIBILITY_TEXT,
} from '@client/definitions/constants';
import { DatePickerRange } from '@client/components/formComponents/datePickerRange/DatePickerRange';
import styles from './formFundraiser.scss';

class FormFundraiser extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isDirty: false,
    };
  }

  handleOnImageCrop = (imgCanvas) => {
    imgCanvas?.toBlob((blob) => {
      const blobSize = blob?.size;
      const maxImgSize = 1999999;
      if (blobSize < maxImgSize) {
        this.props.formProps.setFieldValue('image', blob);
      } else {
        const quality = maxImgSize / blobSize;
        const qualityIncrease = (1 - quality) / 3;
        const qualityArgument = quality + qualityIncrease;
        imgCanvas.toBlob(
          (blobToCompress) => {
            this.props.formProps.setFieldValue('image', blobToCompress);
          },
          'image/jpeg',
          qualityArgument
        );
      }
    }, 'image/jpeg');
  };

  handleTogglePreview = async () => {
    const { onTogglePreview, formProps } = this.props;

    if (!formProps.values.acceptContentResponsibility) {
      await formProps.setFieldTouched(
        'acceptContentResponsibility',
        true,
        true
      );

      return;
    }

    const startDate = formProps?.values?.startDate;
    const endDate = formProps?.values?.endDate;

    onTogglePreview({
      ...formProps.values,
      startDate: startDate,
      endDate: endDate,
      fundraiserType: getFundraiserTypeId(formProps.values.fundraiserType),
    });
  };

  handleDescriptionOnChange = (value) => {
    const { isDirty } = this.state;
    if (!isDirty) this.setState({ isDirty: true });
    this.props.formProps.setFieldValue('description', value);
  };

  handleAcceptContentResponsibilityChange = (e) => {
    const checked = e.target.checked;

    const { formProps } = this.props;
    formProps.setFieldValue('acceptContentResponsibility', checked);
    formProps.setFieldTouched('acceptContentResponsibility', true, false);

    if (checked) {
      formProps.setFieldError('acceptContentResponsibility', undefined);
    }
  };

  render() {
    const {
      className,
      formProps,
      translations,
      defaultDescriptionText,
      location,
    } = this.props;

    const { isDirty } = this.state;

    const formValues = {
      ...formProps.values,
      memorialBirthDate:
        formProps.values.memorialBirthDate &&
        formProps.values.memorialBirthDate.substring(0, 10),
      memorialDeathDate:
        formProps.values.memorialDeathDate &&
        formProps.values.memorialDeathDate.substring(0, 10),
      fundraiserType: getFundraiserTypeId(formProps.values.fundraiserType),
    };
    const formErrors = formProps.errors;
    const { image } = formValues;

    const descriptionValue =
      isDirty || formValues?.description
        ? formValues?.description
        : defaultDescriptionText;

    const isMemorial = isMemorialFundraiser(formValues.fundraiserType);
    const relatedToSportevent = isSporteventFundraiser(
      formValues.fundraiserType
    )
      ? formValues.eventName
      : undefined;

    const isNewFundraiser = getSearchQueryValue(location?.search, 'new');
    const setStepNumber = (number) => (isNewFundraiser ? number + 1 : number);

    return (
      <form
        onSubmit={formProps.handleSubmit}
        autoComplete={'off'}
        className={`${styles.formFundraiser}${
          className ? ` ${className}` : ''
        }`}
      >
        <h3 className={`${styleHelper.titleMdPrimary} ${styles.title}`}>
          Redigera insamling
        </h3>
        <div className={styles.formSections}>
          <FormSectionContainer
            number={setStepNumber(1)}
            title={'Information om din insamling'}
          >
            <p className={styles.subTitle}>
              {getIn(translations, 'fundraiserForm1Description')}
            </p>
            <div className={styleHelper.formRow}>
              <InputGroup
                labelText={'Namn på min insamling*'}
                name={'title'}
                value={valueOrEmptyStr(formValues.title)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.title}
              />
              <InputGroup
                labelText={'Insamlingsmål*'}
                name={'targetAmount'}
                value={valueOrEmptyStr(formValues.targetAmount)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.targetAmount}
              />
            </div>
            <div className={styleHelper.formRow}>
              <DatePickerRange
                className={styles.datePicker}
                startDateName={'startDate'}
                startDateLabel={FUNDRAISER_START_DATE}
                endDateName={'endDate'}
                endDateLabel={FUNDRAISER_END_DATE}
                endDatePlaceholder={FUNDRAISER_CURRENT}
                minDate={new Date()}
              />
            </div>

            <LabelWrapper
              labelText={'Beskrivning av min insamling'}
              errorLabel={formErrors.description}
            >
              <Markdown
                value={descriptionValue}
                onChange={this.handleDescriptionOnChange}
                viewAsTextarea={true}
              />
            </LabelWrapper>
            {relatedToSportevent && (
              <p
                className={`${styleHelper.titleSmPrimary} ${styles.smallTitle}`}
              >
                Denna insamling startades i samband med{' '}
                {relatedToSportevent
                  .toString()
                  .replace('-', ' ')
                  .replace(/^\w/, (first) => first.toUpperCase())}
              </p>
            )}
          </FormSectionContainer>
          <FormSectionContainer
            number={setStepNumber(2)}
            title={'Ladda upp en bild'}
          >
            <ImageUploader src={image} handleCrop={this.handleOnImageCrop} />
          </FormSectionContainer>
          {isMemorial && (
            <FormSectionContainer
              title={'Minnesfond'}
              number={setStepNumber(3)}
            >
              <div className={`${styles.sectionRow} ${styleHelper.grid12}`}>
                <div>
                  <InputGroup
                    name="memorialFirstName"
                    labelText="Avlidnes förnamn*"
                    onChange={formProps.handleChange}
                    onBlur={formProps.handleBlur}
                    value={formValues.memorialFirstName}
                    errorLabel={formErrors.memorialFirstName}
                  />
                </div>
                <div>
                  <InputGroup
                    name="memorialLastName"
                    labelText="Avlidnes efternamn*"
                    onChange={formProps.handleChange}
                    onBlur={formProps.handleBlur}
                    value={formValues.memorialLastName}
                    errorLabel={formErrors.memorialLastName}
                  />
                </div>
                <div>
                  <InputGroup
                    name="memorialBirthDate"
                    labelText="Födelsedatum*"
                    placeholder={'YYYY-MM-DD'}
                    onChange={formProps.handleChange}
                    onBlur={formProps.handleBlur}
                    maxLength={10}
                    value={formValues.memorialBirthDate}
                    errorLabel={formErrors.memorialBirthDate}
                  />
                </div>
                <div>
                  <InputGroup
                    name="memorialDeathDate"
                    labelText="Dödsdatum*"
                    placeholder={'YYYY-MM-DD'}
                    onChange={formProps.handleChange}
                    onBlur={formProps.handleBlur}
                    maxLength={10}
                    value={formValues.memorialDeathDate}
                    errorLabel={formErrors.memorialDeathDate}
                  />
                </div>
              </div>
            </FormSectionContainer>
          )}
          <FormSectionContainer
            number={isMemorial ? setStepNumber(4) : setStepNumber(3)}
            title={'Samtycke'}
          >
            <div className={styles.policyTextContainer}>
              <ContentResponsibilityPolicyText />
            </div>
            <div className={styles.checkbox}>
              <Checkbox
                name={'acceptContentResponsibility'}
                isChecked={formValues.acceptContentResponsibility}
                onChange={this.handleAcceptContentResponsibilityChange}
                rightLabel={FUNDRAISER_ACCEPT_CONTENT_RESPONSIBILITY_LABEL}
                errorLabel={
                  formProps.touched.acceptContentResponsibility
                    ? formErrors.acceptContentResponsibility
                    : undefined
                }
              />
              <p>{FUNDRAISER_ACCEPT_CONTENT_RESPONSIBILITY_TEXT}</p>
            </div>
          </FormSectionContainer>
          <FormSectionContainer
            number={isMemorial ? setStepNumber(5) : setStepNumber(4)}
            title={'Förhandsgranska insamling'}
          >
            <p className={styles.subTitle}>
              {getIn(translations, 'fundraiserForm3Description')}
              <a
                className={styles.emailLink}
                href={`mailto:${getIn(
                  translations,
                  'fundraiserForm3DescriptionEmail'
                )}`}
              >
                {getIn(translations, 'fundraiserForm3DescriptionEmail')}
              </a>
            </p>
            <div className={styles.buttonContainer}>
              <button
                className={`${styleHelper.btnPrimaryMd} ${styles.previewBtn}`}
                type="button"
                onClick={this.handleTogglePreview}
              >
                Förhandsgranska
              </button>
            </div>
          </FormSectionContainer>
        </div>
        <span className={`${styles.requiredInfoText}`}>
          {getIn(translations, 'formAsterisk')}
        </span>
      </form>
    );
  }
}

FormFundraiser.propTypes = {
  location: PropTypes.object,
  translations: ImmutablePropTypes.map,
  defaultDescriptionText: PropTypes.string,
  className: PropTypes.string,
  formProps: PropTypes.object,
  onTogglePreview: PropTypes.func,
};

export default FormFundraiser;
